import React, { useEffect, useState } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    MenuItem,
    InputAdornment,
    IconButton,
    Switch,
    Slider,
    Collapse,
    Typography,
} from "@material-ui/core";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import LockIcon from "@material-ui/icons/LockOutlined";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import LogoLoader from "../LogoLoader";
import {
    MODELS,
    VOICES,
    TEXT_VOICE,
    isVoice,
    PROMPT_MAX_CHARS,
} from "./promptOptions";

const useStyles = makeStyles((theme) => ({
    paper: { borderRadius: 16 },
    title: { paddingBottom: 4, "& h2": { fontWeight: 600 } },
    content: { display: "flex", flexDirection: "column", paddingTop: theme.spacing(0.5) },
    section: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        padding: theme.spacing(1.75, 0),
        borderBottom: `1px dashed ${theme.palette.divider}`,
        "&:last-child": { borderBottom: 0 },
    },
    sectionHead: { display: "flex", alignItems: "baseline", gap: theme.spacing(1), flexWrap: "wrap" },
    sectionTitle: { fontSize: 14, fontWeight: 600, color: theme.palette.text.primary },
    sectionHint: { fontSize: 12.5, color: theme.palette.text.secondary },
    fields: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: theme.spacing(0.5, 1.5),
        [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    fields3: {
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: theme.spacing(0.5, 1.5),
        [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    field: { "& .MuiOutlinedInput-root": { borderRadius: 10 } },
    counter: { fontSize: 12, color: theme.palette.text.secondary, textAlign: "right", marginTop: -4 },
    keySaved: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.success.main,
    },
    segment: {
        display: "inline-flex",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 10,
        overflow: "hidden",
        alignSelf: "flex-start",
    },
    segmentButton: {
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 0,
        padding: theme.spacing(0.75, 2),
        color: theme.palette.text.secondary,
    },
    segmentActive: {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
        "&:hover": { backgroundColor: theme.palette.primary.dark },
    },
    switchRow: { display: "flex", alignItems: "flex-start", gap: theme.spacing(1) },
    switchText: { paddingTop: 6 },
    switchLabel: { fontSize: 14, fontWeight: 600 },
    advancedToggle: {
        textTransform: "none",
        fontWeight: 600,
        color: theme.palette.text.secondary,
        alignSelf: "flex-start",
    },
    expand: { transition: "transform .15s" },
    expandOpen: { transform: "rotate(180deg)" },
    sliderBox: { padding: theme.spacing(0, 1) },
    sliderLabel: { display: "flex", justifyContent: "space-between", fontSize: 12.5, color: theme.palette.text.secondary },
    sliderValue: { fontWeight: 700, color: theme.palette.text.primary },
    sliderEnds: { display: "flex", justifyContent: "space-between", fontSize: 11.5, color: theme.palette.text.secondary, marginTop: -6 },
    actions: { padding: theme.spacing(1.5, 3) },
    btnWrapper: { position: "relative", textTransform: "none", fontWeight: 600, borderRadius: 10 },
    cancel: { textTransform: "none", fontWeight: 600, borderRadius: 10 },
    buttonProgress: { position: "absolute", top: "50%", left: "50%", marginTop: -12, marginLeft: -12 },
    callout: {
        fontSize: 12.5,
        lineHeight: 1.45,
        padding: theme.spacing(1, 1.25),
        borderRadius: 8,
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
}));

// Mesmos limites do backend (validatePromptData.ts). Na edição as chaves
// podem ficar em branco: o backend mantém as salvas.
const buildPromptSchema = ({ isEdit, hasVoiceKey }) => Yup.object().shape({
    name: Yup.string().trim().max(100, "Muito longo! (máximo 100)").required("Informe o nome"),
    prompt: Yup.string()
        .max(PROMPT_MAX_CHARS, "Muito longo! (máximo 20.000)")
        .required("Descreva como a IA deve se comportar"),
    queueId: Yup.number().typeError("Escolha o departamento").nullable().required("Escolha o departamento"),
    apiKey: isEdit
        ? Yup.string().max(300, "API Key inválida")
        : Yup.string().trim().max(300, "API Key inválida").required("Informe a API Key"),
    voiceKey: Yup.string()
        .max(300, "Chave inválida")
        .test("voice-key", "Informe a chave do Azure", function (value) {
            if (!isVoice(this.parent.voice) || hasVoiceKey) return true;
            return Boolean(value && value.trim());
        }),
    voiceRegion: Yup.string()
        .nullable()
        .max(50, "Região inválida")
        .test("voice-region", "Informe a região do Azure", function (value) {
            if (!isVoice(this.parent.voice)) return true;
            return Boolean(value && value.trim());
        }),
    temperature: Yup.number()
        .typeError("Informe um número")
        .required("Informe a temperatura")
        .min(0, "Mínimo: 0")
        .max(2, "Máximo: 2"),
    maxTokens: Yup.number()
        .typeError("Informe um número")
        .required("Informe o número máximo de tokens")
        .min(50, "Mínimo: 50")
        .max(4000, "Máximo: 4000")
        .integer("Deve ser um número inteiro"),
    maxMessages: Yup.number()
        .typeError("Informe um número")
        .required("Informe o número máximo de mensagens")
        .min(1, "Mínimo: 1")
        .max(100, "Máximo: 100")
        .integer("Deve ser um número inteiro"),
});

const ADVANCED_FIELDS = ["temperature", "maxTokens", "maxMessages"];

const initialState = {
    name: "",
    prompt: "",
    voice: TEXT_VOICE,
    voiceKey: "",
    voiceRegion: "",
    model: "gpt-4o-mini",
    maxTokens: 100,
    temperature: 1,
    apiKey: "",
    queueId: "",
    maxMessages: 10,
    allowScheduling: false,
};

const Section = ({ title, hint, children }) => {
    const classes = useStyles();
    return (
        <section className={classes.section}>
            <div className={classes.sectionHead}>
                <span className={classes.sectionTitle}>{title}</span>
                {hint && <span className={classes.sectionHint}>{hint}</span>}
            </div>
            {children}
        </section>
    );
};

const PromptModal = ({ open, onClose, promptId }) => {
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

    const [prompt, setPrompt] = useState(initialState);
    const [loadingData, setLoadingData] = useState(false);
    const [queues, setQueues] = useState([]);
    const [showApiKey, setShowApiKey] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get("/queue");
                setQueues(Array.isArray(data) ? data : []);
            } catch (err) {
                toastError(err);
            }
        })();
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (!promptId) {
            setPrompt(initialState);
            return;
        }
        (async () => {
            setLoadingData(true);
            try {
                const { data } = await api.get(`/prompt/${promptId}`);
                setPrompt({
                    ...initialState,
                    ...data,
                    voice: data.voice || TEXT_VOICE,
                    voiceRegion: data.voiceRegion || "",
                    queueId: data.queueId || "",
                    apiKey: "",
                    voiceKey: "",
                });
            } catch (err) {
                toastError(err);
            } finally {
                setLoadingData(false);
            }
        })();
    }, [promptId, open]);

    const handleClose = () => {
        setPrompt(initialState);
        setShowApiKey(false);
        setAdvancedOpen(false);
        onClose();
    };

    const handleSavePrompt = async (values) => {
        try {
            if (promptId) {
                await api.put(`/prompt/${promptId}`, values);
            } else {
                await api.post("/prompt", values);
            }
            toast.success(i18n.t("promptModal.success"));
            handleClose();
        } catch (err) {
            // Mantém o modal aberto para não perder o que foi digitado.
            toastError(err);
        }
    };

    const fieldProps = (name, touched, errors, extra = {}) => ({
        name,
        variant: "outlined",
        margin: "dense",
        fullWidth: true,
        className: classes.field,
        error: Boolean(touched[name] && errors[name]),
        helperText: (touched[name] && errors[name]) || extra.helperText,
        ...extra,
    });

    const modelOptions = MODELS.some((m) => m.value === prompt.model) || !prompt.model
        ? MODELS
        : [...MODELS, { value: prompt.model, label: prompt.model, hint: "Modelo atual" }];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            scroll="paper"
            fullWidth
            fullScreen={fullScreen}
            classes={{ paper: fullScreen ? undefined : classes.paper }}
        >
            {loadingData && <LogoLoader overlay />}
            <DialogTitle className={classes.title}>
                {promptId ? "Editar assistente" : "Novo assistente"}
            </DialogTitle>
            <Formik
                initialValues={prompt}
                enableReinitialize={true}
                validationSchema={buildPromptSchema({
                    isEdit: Boolean(promptId),
                    hasVoiceKey: Boolean(prompt.voiceKeyHint),
                })}
                onSubmit={async (values) => {
                    // Aguarda a requisição: o Formik mantém isSubmitting=true e o
                    // botão desabilitado, evitando envio duplicado.
                    await handleSavePrompt(values);
                }}
            >
                {({ touched, errors, isSubmitting, values, setFieldValue, submitCount }) => {
                    const voiceMode = isVoice(values.voice);
                    const advancedHasError = submitCount > 0 && ADVANCED_FIELDS.some((f) => errors[f]);
                    const showAdvanced = advancedOpen || advancedHasError;
                    const promptLength = (values.prompt || "").length;

                    return (
                        <Form style={{ display: "contents" }}>
                            <DialogContent dividers className={classes.content}>
                                <Section title="Identificação">
                                    <div className={classes.fields}>
                                        <Field
                                            as={TextField}
                                            label="Nome"
                                            autoFocus={!promptId}
                                            {...fieldProps("name", touched, errors)}
                                        />
                                        <Field
                                            as={TextField}
                                            select
                                            label="Transferir para o departamento"
                                            {...fieldProps("queueId", touched, errors, {
                                                helperText: "Quando a IA não resolver ou o cliente pedir uma pessoa",
                                            })}
                                        >
                                            {queues.map((q) => (
                                                <MenuItem key={q.id} value={q.id}>
                                                    {q.name}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </div>
                                </Section>

                                <Section title="Instruções da IA" hint="como ela deve se comportar">
                                    <Field
                                        as={TextField}
                                        multiline
                                        minRows={8}
                                        maxRows={18}
                                        placeholder="Ex.: Você é a atendente virtual da Clínica Sorriso. Fale de forma cordial e objetiva. Responda dúvidas sobre horários, convênios e endereço. Nunca informe valores. Se o cliente pedir uma pessoa, transfira."
                                        {...fieldProps("prompt", touched, errors)}
                                    />
                                    <div className={classes.counter}>
                                        {promptLength.toLocaleString("pt-BR")} / {PROMPT_MAX_CHARS.toLocaleString("pt-BR")} caracteres
                                    </div>
                                </Section>

                                <Section title="Modelo e chave da OpenAI">
                                    <div className={classes.fields}>
                                        <Field
                                            as={TextField}
                                            select
                                            label="Modelo"
                                            {...fieldProps("model", touched, errors, {
                                                helperText: (modelOptions.find((m) => m.value === values.model) || {}).hint,
                                            })}
                                        >
                                            {modelOptions.map((m) => (
                                                <MenuItem key={m.value} value={m.value}>
                                                    {m.label}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                        <Field
                                            as={TextField}
                                            label={prompt.apiKeyHint ? "Nova chave da API (opcional)" : "Chave da API"}
                                            type={showApiKey ? "text" : "password"}
                                            autoComplete="off"
                                            placeholder={prompt.apiKeyHint ? `sk-…${prompt.apiKeyHint}` : "sk-..."}
                                            InputLabelProps={prompt.apiKeyHint ? { shrink: true } : undefined}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            aria-label={showApiKey ? "Ocultar chave" : "Mostrar chave"}
                                                            onClick={() => setShowApiKey((v) => !v)}
                                                        >
                                                            {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            {...fieldProps("apiKey", touched, errors, {
                                                helperText: prompt.apiKeyHint ? (
                                                    <span className={classes.keySaved}>
                                                        <LockIcon style={{ fontSize: 13 }} />
                                                        Chave salva. Deixe em branco para manter.
                                                    </span>
                                                ) : (
                                                    "platform.openai.com → API Keys"
                                                ),
                                            })}
                                        />
                                    </div>
                                </Section>

                                <Section title="Forma de resposta">
                                    <div className={classes.segment} role="group" aria-label="Forma de resposta">
                                        <Button
                                            className={`${classes.segmentButton} ${!voiceMode ? classes.segmentActive : ""}`}
                                            aria-pressed={!voiceMode}
                                            onClick={() => setFieldValue("voice", TEXT_VOICE)}
                                        >
                                            Texto
                                        </Button>
                                        <Button
                                            className={`${classes.segmentButton} ${voiceMode ? classes.segmentActive : ""}`}
                                            aria-pressed={voiceMode}
                                            onClick={() => {
                                                if (!voiceMode) {
                                                    setFieldValue(
                                                        "voice",
                                                        isVoice(prompt.voice) ? prompt.voice : VOICES[0].value
                                                    );
                                                }
                                            }}
                                        >
                                            Áudio (voz)
                                        </Button>
                                    </div>
                                    {voiceMode && (
                                        <>
                                            <div className={classes.callout}>
                                                A resposta é enviada como áudio usando o Azure (portal.azure.com → Criar
                                                recurso → Speech). A região é onde o serviço foi criado, ex.: brazilsouth.
                                            </div>
                                            <div className={classes.fields3}>
                                                <Field as={TextField} select label="Voz" {...fieldProps("voice", touched, errors)}>
                                                    {VOICES.map((v) => (
                                                        <MenuItem key={v.value} value={v.value}>
                                                            {v.label}
                                                        </MenuItem>
                                                    ))}
                                                </Field>
                                                <Field
                                                    as={TextField}
                                                    label={prompt.voiceKeyHint ? "Nova chave do Azure (opcional)" : "Chave do Azure"}
                                                    type="password"
                                                    autoComplete="off"
                                                    placeholder={prompt.voiceKeyHint ? `…${prompt.voiceKeyHint}` : undefined}
                                                    InputLabelProps={prompt.voiceKeyHint ? { shrink: true } : undefined}
                                                    {...fieldProps("voiceKey", touched, errors, {
                                                        helperText: prompt.voiceKeyHint ? "Em branco mantém a atual" : undefined,
                                                    })}
                                                />
                                                <Field
                                                    as={TextField}
                                                    label="Região do Azure"
                                                    placeholder="brazilsouth"
                                                    {...fieldProps("voiceRegion", touched, errors)}
                                                />
                                            </div>
                                        </>
                                    )}
                                </Section>

                                <Section title="Agendamento">
                                    <div className={classes.switchRow}>
                                        <Switch
                                            checked={Boolean(values.allowScheduling)}
                                            onChange={(e) => setFieldValue("allowScheduling", e.target.checked)}
                                            color="primary"
                                            inputProps={{ "aria-label": "Permitir agendamento pela IA" }}
                                        />
                                        <div className={classes.switchText}>
                                            <div className={classes.switchLabel}>Permitir agendamento pela IA</div>
                                            <Typography variant="body2" color="textSecondary" style={{ fontSize: 12.5 }}>
                                                Consulta serviços e horários livres da Agenda e marca o horário depois de
                                                confirmar com o cliente. Precisa de serviços e profissionais cadastrados.
                                            </Typography>
                                        </div>
                                    </div>
                                </Section>

                                <section className={classes.section}>
                                    <Button
                                        size="small"
                                        className={classes.advancedToggle}
                                        onClick={() => setAdvancedOpen((v) => !v)}
                                        aria-expanded={showAdvanced}
                                        endIcon={
                                            <ExpandMoreIcon
                                                className={`${classes.expand} ${showAdvanced ? classes.expandOpen : ""}`}
                                            />
                                        }
                                    >
                                        Ajustes avançados
                                    </Button>
                                    <Collapse in={showAdvanced}>
                                        <div className={classes.fields3}>
                                            <div className={classes.sliderBox}>
                                                <div className={classes.sliderLabel}>
                                                    <span id="temperature-label">Criatividade (temperatura)</span>
                                                    <span className={classes.sliderValue}>
                                                        {Number(values.temperature || 0).toLocaleString("pt-BR")}
                                                    </span>
                                                </div>
                                                <Slider
                                                    value={Number(values.temperature) || 0}
                                                    min={0}
                                                    max={2}
                                                    step={0.1}
                                                    aria-labelledby="temperature-label"
                                                    onChange={(_, v) => setFieldValue("temperature", v)}
                                                />
                                                <div className={classes.sliderEnds}>
                                                    <span>Mais direta</span>
                                                    <span>Mais criativa</span>
                                                </div>
                                            </div>
                                            <Field
                                                as={TextField}
                                                label="Tamanho máx. da resposta (tokens)"
                                                type="number"
                                                inputProps={{ min: 50, max: 4000, step: 50 }}
                                                {...fieldProps("maxTokens", touched, errors, {
                                                    helperText: "50 a 4.000 · 1 token ≈ 4 caracteres",
                                                })}
                                            />
                                            <Field
                                                as={TextField}
                                                label="Mensagens de contexto"
                                                type="number"
                                                inputProps={{ min: 1, max: 100, step: 1 }}
                                                {...fieldProps("maxMessages", touched, errors, {
                                                    helperText: "Quantas mensagens anteriores a IA lê (1 a 100)",
                                                })}
                                            />
                                        </div>
                                    </Collapse>
                                </section>
                            </DialogContent>
                            <DialogActions className={classes.actions}>
                                <Button
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                    variant="outlined"
                                    className={classes.cancel}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    disableElevation
                                    className={classes.btnWrapper}
                                >
                                    {promptId ? "Salvar alterações" : "Criar assistente"}
                                    {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                                </Button>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};

export default PromptModal;
